import {ChevronIcon} from "assets/icons";
import {
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

interface Props {
  value: string;
  dontCloseOnSelect?: boolean;
  /** Replaces the entire trigger button content. Can be a ReactNode or a render function receiving the open state. */
  triggerContent?: ReactNode | ((open: boolean) => ReactNode);
  triggerClassName?: string | ((open: boolean) => string);
}

export interface DropdownRef {
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const Dropdown = forwardRef<DropdownRef, PropsWithChildren<Props>>(
  ({value, children, dontCloseOnSelect, triggerContent, triggerClassName}, ref) => {
    const [height, setHeight] = useState(0);
    const contentWrapper = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (!contentWrapper.current) return;
      const handler = () => {
        setHeight(contentWrapper.current?.scrollHeight || 0);
      };

      handler();
      const observer = new ResizeObserver(handler);
      observer.observe(contentWrapper.current);

      return () => {
        observer.disconnect();
      };
    }, [contentWrapper]);

    const dropdownStyle = useMemo(
      () => ({
        height: open ? height : 0,
        opacity: open ? 1 : 0,
        pointerEvents: (open ? "all" : "none") as any,
      }),
      [height, open],
    );

    useImperativeHandle(
      ref,
      () => ({
        setOpen,
      }),
      [],
    );

    useEffect(() => {
      if (!open) return;
      const handler = () => {
        setOpen(false);
      };

      document.body.addEventListener("click", handler);

      return () => {
        document.body.removeEventListener("click", handler);
      };
    }, [open]);

    const defaultTriggerClass =
      "flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border border-brand-border bg-white text-brand-navy hover:border-brand-navy transition-colors";

    const resolvedTriggerClass =
      typeof triggerClassName === "function" ? triggerClassName(open) : triggerClassName || defaultTriggerClass;
    const resolvedTriggerContent = typeof triggerContent === "function" ? triggerContent(open) : triggerContent;

    return (
      <div onClick={(e) => e.stopPropagation()} className="flex flex-col relative z-[10]">
        <button
          onClick={() => {
            setOpen((old) => !old);
          }}
          className={resolvedTriggerClass}
        >
          {resolvedTriggerContent ?? (
            <>
              <span>{value}</span>
              <div className="rounded-full bg-white flex items-center justify-center">
                <ChevronIcon
                  className="transform transition-transform"
                  style={{transform: !open ? "rotate(180deg)" : "rotate(0deg)"}}
                />
              </div>
            </>
          )}
        </button>
        <div
          onClick={(e) => e.stopPropagation()}
          onClickCapture={() => {
            !dontCloseOnSelect && setOpen(false);
          }}
          ref={contentWrapper}
          className="overflow-hidden w-max transition-all mt-1 absolute top-full right-0 z-10"
          style={dropdownStyle}
        >
          {children}
        </div>
      </div>
    );
  },
);

export default Dropdown;
